
import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

// Import regular components
import ProtectedRoute from './ProtectedRoute';
import GuestRoute from './GuestRoute';
import AdminRoute from './AdminRoute';
import PrincipalRoute from './PrincipalRoute';
import CashierRoute from './CashierRoute';
import AdminCashierRoute from './AdminCashierRoute';
import AdminPrincipalFormMasterRoute from './AdminPrincipalFormMasterRoute';
import TeacherFormMasterRoute from './TeacherFormMasterRoute';
import About from './About';
import Contact from './Contact';
import Login from './Login';
import Signup from './Signup';
import StudentRegistration from './StudentRegistration';
import AdmissionManagement from './AdmissionManagement';
import AdmissionLetter from './AdmissionLetter';
import GenerateReports from './GenerateReports';
import TeacherOversight from './TeacherOversight';
import FeeManagement from './FeeManagement';
import AssignUser from './AssignUser';
import ScoreSheet from './ScoreSheet';
import TraitsAssessment from './TraitsAssessment';
import Academics from './Academics';
import ApproveUser from './ApproveUser';
import ImageUpload from './ImageUpload';
import MasterReportSheet from './MasterReportSheet';
import PromoteStudents from './PromoteStudents';
import PaymentTrack from './PaymentTrack';
import CBT from './CBT';
import Payment from './Payment';
import ViewCBTScores from './ViewCBTScores';
import ViewCBTTestScores from './ViewCBTTestScores';
import SchoolFeesTrack from '../pages/SchoolFeesTrack';
import AssignChildrenToParentPage from '../pages/AssignChildrenToParentPage';
import StockManagementPage from '../pages/StockManagementPage';
import Messaging from './Messaging';
import MessageDetail from './MessageDetail';
import PaymentCallback from './PaymentCallback';
import RefundManagement from './RefundManagement';
import StudentRefunds from './StudentRefunds';

// Lazy-loaded components
const NewLandingPage = React.lazy(() => import('./NewLandingPage'));
const Home = React.lazy(() => import('./Home'));
const AdminHomepage = React.lazy(() => import('./AdminHomepage'));
const StudentHomepage = React.lazy(() => import('./StudentHomepage'));
const TeacherHomepage = React.lazy(() => import('./TeacherHomepage'));
const ParentHomepage = React.lazy(() => import('./ParentHomepage'));
const CashierHomepage = React.lazy(() => import('./CashierHomepage'));
const PrincipalHomepage = React.lazy(() => import('./PrincipalHomepage'));
const FormMasterHomepage = React.lazy(() => import('./FormMasterHomepage'));
const SystemSettings = React.lazy(() => import('./SystemSettings'));
const ForgotPassword = React.lazy(() => import('./ForgotPassword'));
const ChangePassword = React.lazy(() => import('./ChangePassword'));
const Announcements = React.lazy(() => import('./Announcements'));
const AnnouncementUpload = React.lazy(() => import('./AnnouncementUpload'));
const Assignments = React.lazy(() => import('./Assignments'));
const AssignmentDetails = React.lazy(() => import('./AssignmentDetails'));
const AssignmentManagement = React.lazy(() => import('./AssignmentManagement'));
const AttendanceTracking = React.lazy(() => import('./AttendanceTracking'));
const CBTTest = React.lazy(() => import('./CBTTest'));
const ClassManagement = React.lazy(() => import('./ClassManagement'));
const ClassManager = React.lazy(() => import('./ClassManager'));
const ClassAssignment = React.lazy(() => import('./ClassAssignment'));
const CreateAssignment = React.lazy(() => import('./CreateAssignment'));
const CreateTimetable = React.lazy(() => import('./CreateTimetable'));
const Downloads = React.lazy(() => import('./Downloads'));
const Events = React.lazy(() => import('./Events'));
const Exam = React.lazy(() => import('./Exam'));
const ExamList = React.lazy(() => import('./ExamList'));
const ExamManagement = React.lazy(() => import('./ExamManagement'));
const FeesAndPayments = React.lazy(() => import('./FeesAndPayments'));
const Gradebook = React.lazy(() => import('./Gradebook'));
const Grades = React.lazy(() => import('./Grades'));
const ManageUsers = React.lazy(() => import('./ManageUsers'));
const ManageAssignments = React.lazy(() => import('./ManageAssignments'));
const MyChildrensResult = React.lazy(() => import('./MyChildrensResult'));
const MyClasses = React.lazy(() => import('./MyClasses'));
const MyCourses = React.lazy(() => import('./MyCourses'));
const MyStudents = React.lazy(() => import('./MyStudents'));
const Notifications = React.lazy(() => import('./Notifications'));
const Payments = React.lazy(() => import('./Payments'));
const Profile = React.lazy(() => import('./Profile'));
const Results = React.lazy(() => import('./Results'));
const SchoolReports = React.lazy(() => import('./SchoolReports'));
const StudentCBT = React.lazy(() => import('./StudentCBT'));
const StudentCBTTest = React.lazy(() => import('./StudentCBTTest'));
const StudentEnrollment = React.lazy(() => import('./StudentEnrollment'));
const StudentManagement = React.lazy(() => import('./StudentManagement'));
const StudentAssignments = React.lazy(() => import('./StudentAssignments'));
const SubmissionForm = React.lazy(() => import('./SubmissionForm'));
const Support = React.lazy(() => import('./Support'));
const TakeAttendance = React.lazy(() => import('./TakeAttendance'));
const TeacherManagement = React.lazy(() => import('./TeacherManagement'));
const Timetable = React.lazy(() => import('./Timetable'));
const TimetableManagement = React.lazy(() => import('./TimetableManagement'));
const UploadResults = React.lazy(() => import('./UploadResults'));
const InputResults = React.lazy(() => import('./InputResults'));
const UserProfile = React.lazy(() => import('./UserProfile'));
const Users = React.lazy(() => import('./Users'));
const ViewAlerts = React.lazy(() => import('./ViewAlerts'));
const ViewSubmissions = React.lazy(() => import('./ViewSubmissions'));
const ViewSubmittedResults = React.lazy(() => import('./ViewSubmittedResults'));
const Unauthorized = React.lazy(() => import('./Unauthorized'));
const Attendance = React.lazy(() => import('./Attendance'));
const UpdateCA = React.lazy(() => import('./UpdateCA'));
const StaffRegistration = React.lazy(() => import('./StaffRegistration'));
const Registration = React.lazy(() => import('./Registration'));
const CreateNews = React.lazy(() => import('./CreateNews'));
const News = React.lazy(() => import('./News'));
const SessionManagement = React.lazy(() => import('./SessionManagement'));
const SearchResults = React.lazy(() => import('./SearchResults'));
const PrintResultSheet = React.lazy(() => import('./PrintResultSheet'));
const AdminSearchResults = React.lazy(() => import('./AdminSearchResults'));
const SearchStudents = React.lazy(() => import('./SearchStudents'));

const AppRoutes = () => (
    <Suspense fallback={<div className="loading-fullscreen">Loading Page...</div>}>
        <Routes>
            {/* Guest Routes: Accessible only to unauthenticated users */}
            <Route path="/" element={<GuestRoute><NewLandingPage /></GuestRoute>} />
            <Route path="/login" element={<GuestRoute><Login /></GuestRoute>} />
            <Route path="/signup" element={<GuestRoute><Signup /></GuestRoute>} />
            <Route path="/student-registration" element={<GuestRoute><StudentRegistration /></GuestRoute>} />
            <Route path="/forgot-password" element={<GuestRoute><ForgotPassword /></GuestRoute>} />
            <Route path="/contact" element={<GuestRoute><Contact /></GuestRoute>} />
            <Route path="/about" element={<GuestRoute><About /></GuestRoute>} />

            {/* Protected Routes: Accessible only to authenticated users */}
            <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
            <Route path="/announcements" element={<ProtectedRoute><Announcements /></ProtectedRoute>} />
            <Route path="/events" element={<ProtectedRoute><Events /></ProtectedRoute>} />
            <Route path="/downloads" element={<ProtectedRoute><Downloads /></ProtectedRoute>} />
            <Route path="/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
            <Route path="/staff-registration" element={<ProtectedRoute><StaffRegistration /></ProtectedRoute>} />
            <Route path="/registration" element={<ProtectedRoute><Registration /></ProtectedRoute>} />
            <Route path="/create-news" element={<ProtectedRoute roles={['admin', 'principal', 'form-master']}><CreateNews /></ProtectedRoute>} />
            <Route path="/news" element={<ProtectedRoute><News /></ProtectedRoute>} />
            <Route path="/session-management" element={<ProtectedRoute><SessionManagement /></ProtectedRoute>} />
            <Route path="/school-fees-track" element={<ProtectedRoute><SchoolFeesTrack /></ProtectedRoute>} />
            <Route path="/stock-management" element={<ProtectedRoute roles={['admin', 'cashier']}><StockManagementPage /></ProtectedRoute>} />

            {/* Admin Routes */}
            <Route path="/admin-homepage" element={<AdminRoute><AdminHomepage /></AdminRoute>} />
            <Route path="/admin/search-results" element={<AdminRoute><AdminSearchResults /></AdminRoute>} />
            <Route path="/approve-user" element={<AdminRoute><ApproveUser /></AdminRoute>} />
            <Route path="/system-settings" element={<AdminRoute><SystemSettings /></AdminRoute>} />
            <Route path="/manage-users" element={<AdminRoute><ManageUsers /></AdminRoute>} />
            <Route path="/student-management" element={<AdminRoute><StudentManagement /></AdminRoute>} />
            <Route path="/teacher-management" element={<AdminRoute><TeacherManagement /></AdminRoute>} />
            <Route path="/class-management" element={<AdminRoute><ClassManagement /></AdminRoute>} />
            <Route path="/class-manager" element={<AdminRoute><ClassManager /></AdminRoute>} />
            <Route path="/class-assignment" element={<AdminRoute><ClassAssignment /></AdminRoute>} />
            <Route path="/timetable-management" element={<AdminRoute><TimetableManagement /></AdminRoute>} />
            <Route path="/exam-management" element={<AdminRoute><ExamManagement /></AdminRoute>} />
            <Route path="/upload-results" element={<AdminRoute><UploadResults /></AdminRoute>} />
            <Route path="/announcement-upload" element={<AdminRoute><AnnouncementUpload /></AdminRoute>} />
            <Route path="/school-reports" element={<AdminRoute><SchoolReports /></AdminRoute>} />
            <Route path="/student-enrollment" element={<AdminRoute><StudentEnrollment /></AdminRoute>} />
            <Route path="/users" element={<AdminRoute><Users /></AdminRoute>} />
            <Route path="/user-profile/:id" element={<AdminRoute><UserProfile /></AdminRoute>} />
            <Route path="/admission-management" element={<AdminRoute><AdmissionManagement /></AdminRoute>} />
            <Route path="/admission-letter/:studentId" element={<ProtectedRoute><AdmissionLetter /></ProtectedRoute>} />
            <Route path="/reports" element={<AdminRoute><GenerateReports /></AdminRoute>} />
            <Route path="/assign-user" element={<AdminRoute><AssignUser /></AdminRoute>} />
            <Route path="/assign-children" element={<AdminRoute><AssignChildrenToParentPage /></AdminRoute>} />
            <Route path="/admin/refunds" element={<AdminRoute><RefundManagement /></AdminRoute>} />

            {/* Principal Routes */}
            <Route path="/principal-homepage" element={<PrincipalRoute><PrincipalHomepage /></PrincipalRoute>} />
            <Route path="/teacher-oversight" element={<PrincipalRoute><TeacherOversight /></PrincipalRoute>} />

            {/* Cashier Routes */}
            <Route path="/cashier-homepage" element={<CashierRoute><CashierHomepage /></CashierRoute>} />
            <Route path="/fee-management" element={<AdminCashierRoute><FeeManagement /></AdminCashierRoute>} />
            <Route path="/payments" element={<AdminCashierRoute><Payments /></AdminCashierRoute>} />
            <Route path="/payment" element={<AdminCashierRoute><Payment /></AdminCashierRoute>} />
            <Route path="/fees" element={<CashierRoute><FeesAndPayments /></CashierRoute>} />
            <Route path="/search-results" element={<CashierRoute><SearchResults /></CashierRoute>} />

            {/* Teacher Routes */}
            <Route path="/teacher-homepage" element={<ProtectedRoute roles={['teacher']}><TeacherHomepage /></ProtectedRoute>} />
            <Route path="/my-students" element={<ProtectedRoute roles={['teacher', 'form-master']}><MyStudents /></ProtectedRoute>} />
            <Route path="/gradebook" element={<ProtectedRoute roles={['teacher']}><Gradebook /></ProtectedRoute>} />
            <Route path="/attendance-tracking" element={<ProtectedRoute roles={['teacher', 'form-master']}><AttendanceTracking /></ProtectedRoute>} />
            <Route path="/assignment-management" element={<ProtectedRoute roles={['teacher']}><AssignmentManagement /></ProtectedRoute>} />
            <Route path="/create-assignment" element={<ProtectedRoute roles={['teacher']}><CreateAssignment /></ProtectedRoute>} />
            <Route path="/my-classes" element={<ProtectedRoute roles={['teacher']}><MyClasses /></ProtectedRoute>} />
            <Route path="/class/:className/results" element={<ProtectedRoute roles={['teacher', 'form-master']}><InputResults /></ProtectedRoute>} />
            <Route path="/class/:className/assignments" element={<ProtectedRoute roles={['teacher']}><ManageAssignments /></ProtectedRoute>} />
            <Route path="/class/:className/attendance" element={<ProtectedRoute roles={['teacher', 'form-master']}><TakeAttendance /></ProtectedRoute>} />

            {/* Student Routes */}
            <Route path="/student-homepage" element={<ProtectedRoute roles={['student']}><StudentHomepage /></ProtectedRoute>} />
            <Route path="/my-courses" element={<ProtectedRoute roles={['student']}><MyCourses /></ProtectedRoute>} />
            <Route path="/assignments" element={<ProtectedRoute roles={['student']}><Assignments /></ProtectedRoute>} />
            <Route path="/assignment-details/:id" element={<ProtectedRoute roles={['student']}><AssignmentDetails /></ProtectedRoute>} />
            <Route path="/submission-form/:id" element={<ProtectedRoute roles={['student']}><SubmissionForm /></ProtectedRoute>} />
            <Route path="/grades" element={<ProtectedRoute roles={['student']}><Grades /></ProtectedRoute>} />
            <Route path="/results" element={<ProtectedRoute roles={['student']}><Results /></ProtectedRoute>} />
            <Route path="/timetable" element={<ProtectedRoute roles={['student']}><Timetable /></ProtectedRoute>} />
            <Route path="/student-assignments/:className" element={<ProtectedRoute roles={['student']}><StudentAssignments /></ProtectedRoute>} />
            <Route path="/exam/:examId" element={<ProtectedRoute roles={['student']}><Exam /></ProtectedRoute>} />
            <Route path="/exam-list" element={<ProtectedRoute roles={['student']}><ExamList /></ProtectedRoute>} />
            <Route path="/student-fees" element={<ProtectedRoute roles={['student']}><FeesAndPayments /></ProtectedRoute>} />
            <Route path="/student-payment" element={<ProtectedRoute roles={['student']}><Payment /></ProtectedRoute>} />
            <Route path="/payment-track" element={<ProtectedRoute roles={['student']}><PaymentTrack /></ProtectedRoute>} />
            <Route path="/payment-callback" element={<ProtectedRoute><PaymentCallback /></ProtectedRoute>} />
            <Route path="/my-refunds" element={<ProtectedRoute roles={['student', 'parent']}><StudentRefunds /></ProtectedRoute>} />

            {/* Parent Routes */}
            <Route path="/parent-homepage" element={<ProtectedRoute roles={['parent']}><ParentHomepage /></ProtectedRoute>} />
            <Route path="/my-childrens-result" element={<ProtectedRoute roles={['parent']}><MyChildrensResult /></ProtectedRoute>} />
            <Route path="/fees-and-payments" element={<ProtectedRoute roles={['parent']}><FeesAndPayments /></ProtectedRoute>} />
            
            {/* Form Master Routes */}
            <Route path="/form-master-homepage" element={<ProtectedRoute roles={['form-master']}><FormMasterHomepage /></ProtectedRoute>} />
            <Route path="/attendance" element={<ProtectedRoute roles={['form-master']}><Attendance /></ProtectedRoute>} />
            <Route path="/create-timetable" element={<ProtectedRoute roles={['form-master']}><CreateTimetable /></ProtectedRoute>} />

            {/* Multi-role Routes */}
            <Route path="/view-alerts" element={<AdminPrincipalFormMasterRoute><ViewAlerts /></AdminPrincipalFormMasterRoute>} />
            <Route path="/view-submitted-results" element={<AdminPrincipalFormMasterRoute><ViewSubmittedResults /></AdminPrincipalFormMasterRoute>} />
            <Route path="/class/:className/assignment/:assignmentId/submissions" element={<TeacherFormMasterRoute><ViewSubmissions /></TeacherFormMasterRoute>} />
            <Route path="/update-ca" element={<TeacherFormMasterRoute><UpdateCA /></TeacherFormMasterRoute>} />
            <Route path="/score-sheet" element={<TeacherFormMasterRoute><ScoreSheet /></TeacherFormMasterRoute>} />
            <Route path="/traits-assessment" element={<TeacherFormMasterRoute><TraitsAssessment /></TeacherFormMasterRoute>} />
            <Route path="/search-students" element={<TeacherFormMasterRoute><SearchStudents /></TeacherFormMasterRoute>} />
            <Route path="/academics" element={<AdminPrincipalFormMasterRoute><Academics /></AdminPrincipalFormMasterRoute>} />
            <Route path="/upload-image" element={<ProtectedRoute><ImageUpload /></ProtectedRoute>} />
            <Route path="/master-report-sheet" element={<AdminPrincipalFormMasterRoute><MasterReportSheet /></AdminPrincipalFormMasterRoute>} />
            <Route path="/promote-students" element={<AdminPrincipalFormMasterRoute><PromoteStudents /></AdminPrincipalFormMasterRoute>} />
            <Route path="/print-result-sheet" element={<ProtectedRoute roles={['admin', 'principal', 'form-master']}><PrintResultSheet /></ProtectedRoute>} />
            
            {/* CBT Routes */}
            <Route path="/cbt" element={<AdminPrincipalFormMasterRoute><CBT /></AdminPrincipalFormMasterRoute>} />
            <Route path="/cbt-test" element={<AdminPrincipalFormMasterRoute><CBTTest /></AdminPrincipalFormMasterRoute>} />
            <Route path="/cbt-test/teacher" element={<TeacherFormMasterRoute><CBTTest /></TeacherFormMasterRoute>} />
            <Route path="/student-cbt" element={<ProtectedRoute><StudentCBT /></ProtectedRoute>} />
            <Route path="/student-cbt-test" element={<ProtectedRoute><StudentCBTTest /></ProtectedRoute>} />
            <Route path="/cbt/exam/:examId" element={<ProtectedRoute><StudentCBT /></ProtectedRoute>} />
            <Route path="/view-cbt-scores" element={<ProtectedRoute roles={['admin', 'principal', 'teacher', 'form-master']}><ViewCBTScores /></ProtectedRoute>} />
            <Route path="/view-cbt-test-scores" element={<ProtectedRoute roles={['admin', 'principal', 'teacher', 'form-master']}><ViewCBTTestScores /></ProtectedRoute>} />
            
            {/* Messaging Routes */}
            <Route path="/messaging" element={<ProtectedRoute><Messaging /></ProtectedRoute>} />
            <Route path="/messaging/message/:messageId" element={<ProtectedRoute><MessageDetail /></ProtectedRoute>} />

            {/* Fallback Route */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    </Suspense>
);

export default AppRoutes;
